/**
 * Collapsible "Add guarantor" form on the customer detail page. Self-gates on
 * `customer.write`. Posts to POST /api/customers/:id/guarantors — the back-office
 * path for attaching a guarantor after onboarding (the dealer app captures them
 * inline at customer-create). On success the guarantors list query is
 * invalidated so the new row appears immediately.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { CreateGuarantorRequest, Guarantor } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

export function AddGuarantorForm({
  customerId,
}: {
  readonly customerId: string;
}): JSX.Element | null {
  const auth = useAuth();
  const canWrite = (auth.user?.permissions ?? []).includes("customer.write");
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [given, setGiven] = useState("");
  const [family, setFamily] = useState("");
  const [phone, setPhone] = useState("");
  const [relationship, setRelationship] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: CreateGuarantorRequest) =>
      api.post<Guarantor>(`/api/customers/${customerId}/guarantors`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["customers", "guarantors", customerId] });
      setGiven("");
      setFamily("");
      setPhone("");
      setRelationship("");
      setError(null);
      setOpen(false);
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function submit(): void {
    setError(null);
    if (given.trim().length < 1 || family.trim().length < 1) {
      setError("Given and family names are required.");
      return;
    }
    if (phone.trim().length < 1) {
      setError("Phone is required.");
      return;
    }
    const relTrimmed = relationship.trim();
    const body: CreateGuarantorRequest = {
      given: given.trim(),
      family: family.trim(),
      phone: phone.trim(),
      ...(relTrimmed.length > 0 ? { relationship: relTrimmed } : {}),
    };
    mutation.mutate(body);
  }

  if (!canWrite) return null;

  if (!open) {
    return (
      <div className="border-t border-slate-100 p-3">
        <Button variant="ghost" onClick={() => setOpen(true)}>
          Add guarantor…
        </Button>
      </div>
    );
  }

  return (
    <div className="m-3 rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">Add guarantor</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Given name">
          <TextInput value={given} onChange={(e) => setGiven(e.target.value)} maxLength={100} />
        </Field>
        <Field label="Family name">
          <TextInput value={family} onChange={(e) => setFamily(e.target.value)} maxLength={100} />
        </Field>
        <Field label="Phone" hint="national format, e.g. 0712 345 678">
          <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        <Field label="Relationship" hint="optional (e.g. spouse, employer)">
          <TextInput
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            maxLength={100}
          />
        </Field>
      </div>
      <FormError message={error} />
      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending}>
          {mutation.isPending ? "Saving…" : "Add guarantor"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setError(null);
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
